import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
} from "react";
import {
  Background,
  Controls,
  Panel,
  ReactFlow,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type EdgeChange,
  type NodeChange,
  type NodeMouseHandler,
  type OnSelectionChangeParams,
  type ReactFlowInstance,
  type XYPosition,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { DailyEdge } from "../Features/Daily/DailyEdge";
import { DailySidebar } from "../Features/Daily/DailySidebar";
import { DailyTextNode } from "../Features/Daily/DailyTextNode";
import {
  createBlankCanvas,
  createId,
  loadDailyWorkspace,
  saveDailyWorkspace,
} from "../Features/Daily/dailyStorage";
import type {
  DailyBoard,
  DailyEdge as DailyEdgeType,
  DailyNode,
  DailyWorkspace,
} from "../Features/Daily/types";

export function Daily() {
  const [workspace, setWorkspace] = useState<DailyWorkspace>(() =>
    loadDailyWorkspace(),
  );
  const flowInstanceRef = useRef<ReactFlowInstance<DailyNode, DailyEdgeType> | null>(
    null,
  );
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    screenX: number;
    screenY: number;
    flowPosition: XYPosition;
  } | null>(null);
  const saveTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = window.setTimeout(() => {
      saveDailyWorkspace(workspace);
    }, 350);

    return () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [workspace]);

  const activeCanvas =
    workspace.canvases.find(
      (canvas) => canvas.id === workspace.activeCanvasId,
    ) ?? workspace.canvases[0];

  const activeBoard = activeCanvas?.board ?? { nodes: [], edges: [] };

  const updateActiveBoard = useCallback(
    (getNextBoard: (board: DailyBoard) => DailyBoard) => {
      setWorkspace((currentWorkspace) => ({
        ...currentWorkspace,
        canvases: currentWorkspace.canvases.map((canvas) =>
          canvas.id === currentWorkspace.activeCanvasId
            ? {
                ...canvas,
                board: getNextBoard(canvas.board),
              }
            : canvas,
        ),
      }));
    },
    [],
  );

  const updateNodeText = useCallback(
    (nodeId: string, text: string) => {
      updateActiveBoard((board) => ({
        ...board,
        nodes: board.nodes.map((node) =>
          node.id === nodeId ? { ...node, data: { ...node.data, text } } : node,
        ),
      }));
    },
    [updateActiveBoard],
  );

  const deleteEdge = useCallback(
    (edgeId: string) => {
      updateActiveBoard((board) => ({
        ...board,
        edges: board.edges.filter((edge) => edge.id !== edgeId),
      }));
    },
    [updateActiveBoard],
  );

  const visibleNodes = useMemo<DailyNode[]>(
    () =>
      activeBoard.nodes.map((node) => ({
        ...node,
        dragHandle: ".daily-node-drag-handle",
        data: {
          ...node.data,
          onTextChange: updateNodeText,
        },
      })),
    [activeBoard.nodes, updateNodeText],
  );

  const visibleEdges = useMemo<DailyEdgeType[]>(
    () =>
      activeBoard.edges.map((edge) => ({
        ...edge,
        type: "dailyEdge" as const,
        data: {
          onDelete: deleteEdge,
          selected: edge.id === selectedEdgeId,
        },
      })),
    [activeBoard.edges, deleteEdge, selectedEdgeId],
  );

  const nodeTypes = useMemo(
    () => ({
      dailyText: DailyTextNode,
    }),
    [],
  );

  const edgeTypes = useMemo(
    () => ({
      dailyEdge: DailyEdge,
    }),
    [],
  );

  const handleNodesChange = useCallback(
    (changes: NodeChange<DailyNode>[]) => {
      updateActiveBoard((board) => ({
        ...board,
        nodes: applyNodeChanges(changes, board.nodes) as DailyNode[],
      }));
    },
    [updateActiveBoard],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<DailyEdgeType>[]) => {
      updateActiveBoard((board) => ({
        ...board,
        edges: applyEdgeChanges(changes, board.edges) as DailyEdgeType[],
      }));
    },
    [updateActiveBoard],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      setContextMenu(null);
      updateActiveBoard((board) => ({
        ...board,
        edges: addEdge(
          {
            ...connection,
            id: createId(),
            type: "dailyEdge",
          },
          board.edges,
        ) as DailyEdgeType[],
      }));
    },
    [updateActiveBoard],
  );

  const handleSelectionChange = useCallback(
    ({ edges }: OnSelectionChangeParams) => {
      setSelectedEdgeId(edges[0]?.id ?? null);
    },
    [],
  );

  const handleNodeContextMenu: NodeMouseHandler = useCallback((event) => {
    event.preventDefault();
    setContextMenu(null);
  }, []);

  function addNode(position?: XYPosition) {
    updateActiveBoard((board) => ({
      ...board,
      nodes: [
        ...board.nodes,
        {
          id: createId(),
          type: "dailyText",
          dragHandle: ".daily-node-drag-handle",
          position:
            position ?? {
              x: 80 + board.nodes.length * 28,
              y: 80 + board.nodes.length * 20,
            },
          data: {
            text: "",
          },
        },
      ],
    }));
  }

  function clearBoard() {
    setContextMenu(null);
    setSelectedEdgeId(null);
    updateActiveBoard(() => ({ nodes: [], edges: [] }));
  }

  function handleCanvasContextMenu(
    event: MouseEvent | ReactMouseEvent<Element>,
  ) {
    event.preventDefault();

    const flowPosition = flowInstanceRef.current?.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    }) ?? {
      x: event.clientX,
      y: event.clientY,
    };

    setContextMenu({
      screenX: event.clientX,
      screenY: event.clientY,
      flowPosition,
    });
  }

  function addNodeFromContextMenu() {
    if (!contextMenu) {
      return;
    }

    addNode(contextMenu.flowPosition);
    setContextMenu(null);
  }

  function selectCanvas(canvasId: string) {
    setWorkspace((currentWorkspace) => ({
      ...currentWorkspace,
      activeCanvasId: canvasId,
    }));
  }

  function createCanvas() {
    setWorkspace((currentWorkspace) => {
      const canvas = createBlankCanvas(
        `Canvas ${currentWorkspace.canvases.length + 1}`,
      );

      return {
        activeCanvasId: canvas.id,
        canvases: [...currentWorkspace.canvases, canvas],
      };
    });
  }

  function renameCanvas(canvasId: string) {
    const canvas = workspace.canvases.find(
      (currentCanvas) => currentCanvas.id === canvasId,
    );
    const nextName = window.prompt("Rename canvas", canvas?.name ?? "");

    if (!nextName?.trim()) {
      return;
    }

    setWorkspace((currentWorkspace) => ({
      ...currentWorkspace,
      canvases: currentWorkspace.canvases.map((currentCanvas) =>
        currentCanvas.id === canvasId
          ? { ...currentCanvas, name: nextName.trim() }
          : currentCanvas,
      ),
    }));
  }

  function deleteCanvas(canvasId: string) {
    if (workspace.canvases.length === 1) {
      return;
    }

    const shouldDelete = window.confirm("Delete this canvas?");

    if (!shouldDelete) {
      return;
    }

    setWorkspace((currentWorkspace) => {
      const canvases = currentWorkspace.canvases.filter(
        (canvas) => canvas.id !== canvasId,
      );
      const activeCanvasId =
        currentWorkspace.activeCanvasId === canvasId
          ? canvases[0].id
          : currentWorkspace.activeCanvasId;

      return {
        activeCanvasId,
        canvases,
      };
    });
  }

  return (
    <main style={styles.shell}>
      <button
        type="button"
        style={styles.hamburgerButton}
        onClick={() => setSidebarOpen((open) => !open)}
        aria-label="Toggle canvas menu"
      >
        ☰
      </button>

      {sidebarOpen && (
        <DailySidebar
          canvases={workspace.canvases}
          activeCanvasId={activeCanvas?.id ?? ""}
          onSelectCanvas={selectCanvas}
          onCreateCanvas={createCanvas}
          onRenameCanvas={renameCanvas}
          onDeleteCanvas={deleteCanvas}
        />
      )}

      <section style={styles.page}>
        <header style={styles.header}>
          <div>
            <h1 style={styles.heading}>{activeCanvas?.name ?? "Daily"}</h1>
            <p style={styles.subtle}>
              {activeBoard.nodes.length} notes, {activeBoard.edges.length}{" "}
              connections
            </p>
          </div>
          <div style={styles.actions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={clearBoard}
            >
              Clear
            </button>
            <button
              type="button"
              style={styles.primaryButton}
              onClick={() => addNode()}
            >
              Add node
            </button>
          </div>
        </header>

        <section style={styles.board}>
          <ReactFlow<DailyNode, DailyEdgeType>
            key={activeCanvas?.id}
            nodes={visibleNodes}
            edges={visibleEdges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={handleConnect}
            onSelectionChange={handleSelectionChange}
            onPaneClick={() => setContextMenu(null)}
            onPaneContextMenu={handleCanvasContextMenu}
            onNodeContextMenu={handleNodeContextMenu}
            onInit={(instance) => {
              flowInstanceRef.current = instance;
            }}
            fitView
          >
            <Background />
            <Controls />
            {contextMenu && (
              <Panel
                position="top-left"
                style={{
                  ...styles.contextMenu,
                  left: contextMenu.screenX,
                  top: contextMenu.screenY,
                }}
              >
                <button
                  type="button"
                  style={styles.contextMenuItem}
                  onClick={addNodeFromContextMenu}
                >
                  Add node
                </button>
                <button
                  type="button"
                  style={styles.contextMenuItemDanger}
                  onClick={clearBoard}
                >
                  Clear canvas
                </button>
              </Panel>
            )}
          </ReactFlow>
        </section>
      </section>
    </main>
  );
}

const styles = {
  shell: {
    width: "min(1280px, 100vw)",
    height: "100svh",
    margin: "0 auto",
    display: "flex",
    textAlign: "left",
    position: "relative",
  },
  hamburgerButton: {
    position: "fixed",
    left: "8px",
    top: "8px",
    zIndex: 80,
    width: "36px",
    height: "36px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    cursor: "pointer",
  },
  page: {
    flex: 1,
    minWidth: 0,
    padding: "24px 16px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  heading: {
    margin: 0,
  },
  subtle: {
    marginTop: "4px",
    color: "var(--text)",
    fontSize: "14px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  primaryButton: {
    border: "1px solid var(--accent-border)",
    borderRadius: "6px",
    background: "var(--accent)",
    color: "white",
    padding: "8px 12px",
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    padding: "8px 12px",
    cursor: "pointer",
  },
  board: {
    flex: 1,
    minHeight: "420px",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    overflow: "hidden",
    background: "var(--bg)",
  },
  contextMenu: {
    position: "fixed",
    zIndex: 90,
    minWidth: "140px",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    boxShadow: "var(--shadow)",
    overflow: "hidden",
    padding: 0,
    margin: 0,
  },
  contextMenuItem: {
    width: "100%",
    border: 0,
    background: "transparent",
    color: "var(--text-h)",
    padding: "9px 12px",
    textAlign: "left",
    cursor: "pointer",
  },
  contextMenuItemDanger: {
    width: "100%",
    border: 0,
    background: "transparent",
    color: "#ef4444",
    padding: "9px 12px",
    textAlign: "left",
    cursor: "pointer",
  },
} satisfies Record<string, CSSProperties>;
